import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ButtonSize,
  ButtonWidth,
  Drawer,
  type DynamicObjectInterface,
  RadioButton,
  Theme,
  Toggle,
  ToggleStyle,
  useSession,
  useTranslation,
  useToast,
  useNextgenRouter,
} from '@nextgen-web-framework/all';
import {
  SubscriptionResponse,
  SubscriptionType,
  updateShowGcoConfirmationModal,
  updateSubscriptionDetails,
  updateTncAccepted,
} from '@commerce-subscription-webcomponents/sdk';
import { uniqueId, type AppDispatch, type AppState } from '@nextgen-web-framework/core';
import { AmwayUserDetails, AmwayUserDetailsStatusEnum } from '@commerce-cart-webcomponents/sdk';
import SearchDownline from '../../../common/SearchDownline/SearchDownline';
import TermsAndCondition from '../../../subscription-checkout/molecules/TermsAndCondition/TermsAndCondition';
import { SubscribeCtaWithIcon } from '../../../common';
import { useUpdateCartDetails } from '../../../common/UpdateCartDetails/useUpdateCartDetails';
import { useEnableProxyOrder } from '../../../common/EnableProxyOrder/useEnableProxyOrder';
import { EnableGroupOrderConsumerType } from '../../../common/EnableGroupOrder/types/propTypes';
import { showToastMsg } from '../../../../utils/utils';
import GcoConfirmationModal from '../../../common/GcoConfirmationModal/GcoConfirmationModal';
import styles from './SubscribeDetailsDrawer.module.scss';
import { SubscribeDetailsPropTypes } from './types/propTypes';

const SubscribeDetailsDrawer = ({
  createSubscription,
  closeDrawer,
  subscriptionAllowedType,
  allowDownlineSubscription,
  isLoading,
}: SubscribeDetailsPropTypes) => {
  const t = useTranslation();
  const { isABO } = useSession();
  const dispatch = useDispatch<AppDispatch>();
  const { addToast, deleteToast } = useToast();
  const { push } = useNextgenRouter();

  const [selectedType, setSelectedType] = useState<SubscriptionType>(SubscriptionType.Manual);
  const [isDownline, setIsDownline] = useState(false);
  const [displaySearchDownline, setDisplaySearchDownline] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<AmwayUserDetails | undefined>();
  const [hideDrawer, setHideDrawer] = useState(false);
  const { tncAccepted, subscriptionDetails, subscriptionsCmsData, showGcoConfirmationModal } = useSelector(
    (state: AppState) => state.commerceSubscription,
  );

  const {
    isModeSwitchNeeded,
    isCartPresent,
    cartId,
    cartVersion,
    subCart,
    currentOrderType,
    newOrderType,
    isCartDetailsFetched,
  } = useEnableProxyOrder({
    consumer: EnableGroupOrderConsumerType.SubscriptionCheckout,
    subscriptionDetails: subscriptionDetails,
  });

  // TODO: Enable this code for GCO Mode
  // const { addToCart, createCart } = useUpdateCartDetails({
  //   enableGroupOrder,
  //   cartId,
  //   cartVersion,
  //   subCart,
  //   currentOrderType,
  //   subscriptionsCmsData,
  // });

  const redirectToCart = () => push('/cart');

  const redirectToSubscriptionDetails = () => {
    if (subscriptionDetails?.id) {
      push(`/subscription-details/${subscriptionDetails.id}`);
    }
  };

  const onSuccess = () => {
    dispatch(updateSubscriptionDetails({}));
    showToastMsg({
      id: `${uniqueId(16)}`,
      addToast,
      deleteToast,
      message: t('subscription.pdp.sop.addTocart.success'),
      theme: Theme.SUCCESS,
      contentType: 'AlertMessage',
      textLink: {
        text: t('subscription.pdp.sop.addTocart.success.linkText'),
        onClick: redirectToCart,
      },
    });
    closeDrawer(true);
  };

  const onFail = () => {
    dispatch(updateSubscriptionDetails({}));
    showToastMsg({
      id: `${uniqueId(16)}`,
      addToast,
      deleteToast,
      message: t('subscription.pdp.sop.addTocart.failureOrCancel'),
      hasBoldText: true,
      theme: Theme.WARNING,
      contentType: 'AlertMessage',
      textLink: {
        text: t('subscription.pdp.sop.addTocart.failureOrCancel.linkText'),
        onClick: redirectToSubscriptionDetails,
      },
    });
    closeDrawer(true);
  };

  const { addToCart, createCart } = useUpdateCartDetails({
    enableGroupOrder: isModeSwitchNeeded,
    cartId,
    cartVersion,
    subCart,
    subscriptionsCmsData,
    newOrderType: isModeSwitchNeeded ? newOrderType : currentOrderType,
    onSuccess,
    onFail,
  });

  const updateUserCart = (subscriptionDetails: SubscriptionResponse) => {
    if (isCartPresent) {
      addToCart(subscriptionDetails);
    } else {
      createCart(subscriptionDetails);
    }
  };

  const setShowModel = (show: boolean) => {
    dispatch(updateShowGcoConfirmationModal(show));
  };

  const onContinueClick = () => {
    setShowModel(false);
    updateUserCart(subscriptionDetails);
  };

  const onCancelClick = () => {
    dispatch(updateSubscriptionDetails({}));
    showToastMsg({
      id: `${uniqueId(16)}`,
      addToast,
      deleteToast,
      message: t('subscription.pdp.sop.addTocart.failureOrCancel'),
      hasBoldText: true,
      theme: Theme.WARNING,
      contentType: 'AlertMessage',
      textLink: {
        text: t('subscription.pdp.sop.addTocart.failureOrCancel.linkText'),
        onClick: redirectToSubscriptionDetails,
      },
    });
    setShowModel(false);
    closeDrawer();
  };

  useEffect(() => {
    setSelectedType(subscriptionAllowedType[0]);
  }, []);

  useEffect(() => {
    if (subscriptionDetails?.type === SubscriptionType.Manual) {
      if (isModeSwitchNeeded) {
        setHideDrawer(true);
        dispatch(updateShowGcoConfirmationModal(true));
      } else {
        updateUserCart(subscriptionDetails);
        // This method is calling in onscuccess and onfail method
        // closeDrawer();
      }
    }
  }, [isCartDetailsFetched]);

  const onCreateSubscription = () => {
    createSubscription(selectedType, selectedProfile);
  };

  const reSetTermsAndCunditions = () => {
    dispatch(updateTncAccepted(false));
  };

  const subscriptionType = () => (
    <div className={styles['subscription-type-container']}>
      {isABO() && (
        <label className={styles['subscription-type-title']}>{t('subscription.subscriptionType.drawerTitle')}</label>
      )}
      <RadioButton
        data-testid="subscription-type-manual"
        theme={selectedType === SubscriptionType.Manual ? Theme.PURPLE : Theme.BLACK}
        label={
          <div
            className={`${styles['radio-btn-label-text']} ${
              selectedType === SubscriptionType.Manual ? styles['rb-selected'] : ''
            }`}
          >
            {t('subscription.subscriptionType.manualSubscription')}
          </div>
        }
        checked={selectedType === SubscriptionType.Manual}
        disabled={
          subscriptionAllowedType.length === 1 && subscriptionAllowedType.indexOf(SubscriptionType.Automatic) !== -1
        }
        onClick={() => {
          setSelectedType(SubscriptionType.Manual);
          reSetTermsAndCunditions();
        }}
      />
      <RadioButton
        data-testid="subscription-type-automatic"
        theme={selectedType === SubscriptionType.Automatic ? Theme.PURPLE : Theme.BLACK}
        label={
          <div
            className={`${styles['radio-btn-label-text']} ${
              selectedType === SubscriptionType.Automatic ? styles['rb-selected'] : ''
            }`}
          >
            {t('subscription.subscriptionType.automaticSubscription')}
          </div>
        }
        checked={selectedType === SubscriptionType.Automatic}
        disabled={
          isDownline ||
          (subscriptionAllowedType.length === 1 && subscriptionAllowedType.indexOf(SubscriptionType.Manual) !== -1)
        }
        onClick={() => {
          setSelectedType(SubscriptionType.Automatic);
          reSetTermsAndCunditions();
        }}
      />
    </div>
  );

  const subscriptionTypeToggle = () =>
    isABO() && allowDownlineSubscription && subscriptionAllowedType.indexOf(SubscriptionType.Manual) !== -1 ? (
      <div className={styles['subscription-for-container']}>
        <div className={styles['subscription-for-container-title']}>{t('subscription.manualsop.whoisthisfor')}</div>
        <Toggle
          toggleStyle={ToggleStyle.TABS}
          tabList={[t('subscription.manualsop.usertype.myself'), t('subscription.manualsop.usertype.downline')]}
          activeTab={isDownline ? 2 : 1}
          onClick={(e: DynamicObjectInterface) => {
            if (e.index === 1 && isDownline) {
              // If user select Myself then we are hiding the SearchDownline component
              setDisplaySearchDownline(false);
              setSelectedType(subscriptionAllowedType[0]);
              setSelectedProfile({ amwayUserId: '' });
              setIsDownline(false);
              reSetTermsAndCunditions();
            } else if (e.index === 2 && !isDownline) {
              // If user select Downline then we are displaying search downline
              setDisplaySearchDownline(true);
              // ABO user can create only Manuals Subscription for downline thats why we are setting selected type to Manual
              setSelectedType(SubscriptionType.Manual);
              setIsDownline(true);
              reSetTermsAndCunditions();
            }
          }}
        />
      </div>
    ) : (
      <></>
    );

  const disableSubscribeCta = () => {
    let flag = false;
    if (selectedType === SubscriptionType.Manual) {
      if (!displaySearchDownline && !tncAccepted) {
        flag = true;
      } else if (selectedProfile?.status === AmwayUserDetailsStatusEnum.Inactive) {
        flag = true;
      } else if (displaySearchDownline && (!tncAccepted || !selectedProfile?.amwayUserId)) {
        flag = true;
      }
    } else if (displaySearchDownline && !selectedProfile?.amwayUserId) {
      flag = true;
    }
    return flag;
  };

  return (
    <>
      {showGcoConfirmationModal || hideDrawer ? (
        <GcoConfirmationModal onCancelClick={onCancelClick} onContinueClick={onContinueClick} />
      ) : (
        <Drawer
          header={
            isABO() ? (
              <div>{t('subscription.subscriptionDetails.drawerTitle')}</div>
            ) : (
              <div>{t('subscription.subscriptionType.drawerTitle')}</div>
            )
          }
          bottomGap={0}
          footer={
            <SubscribeCtaWithIcon
              data-testid="subscribe-test-testid"
              title={t('subscription.subscriptionSelection.subscribeButtonText')}
              isLoading={isLoading}
              size={ButtonSize.MEDIUM}
              onClick={onCreateSubscription}
              buttonWidth={ButtonWidth.FULL_WIDTH}
              disabled={disableSubscribeCta()}
            />
          }
          closeIcon={true}
          closeOnBackdropClick={true}
          onClose={() => {
            closeDrawer();
          }}
          useReactPortal={true}
          handleScroll={false}
          portalContainerId="react-portal-drawer"
          data-testid="subscription-type-drawer-test-id"
        >
          <div className={styles['drawer-dody']}>
            <div className={styles['body-top-container']}>
              {subscriptionTypeToggle()}

              {displaySearchDownline ? (
                <SearchDownline
                  onDownlineSelect={(profile) => {
                    if (profile?.amwayUserId) {
                      setSelectedProfile(profile);
                    } else {
                      setSelectedProfile(undefined);
                    }
                  }}
                  title={t('subscription.manualsop.searchdownline.title')}
                />
              ) : (
                subscriptionType()
              )}
            </div>
            {selectedType === SubscriptionType.Manual && <TermsAndCondition />}
          </div>
        </Drawer>
      )}
    </>
  );
};

export default SubscribeDetailsDrawer;
